import React, { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { Header, Footer, PageShell } from "../components/Layout";
import api from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { ShieldWarning, Trash, ArrowsClockwise, Robot, UserCircle, MagnifyingGlass } from "@phosphor-icons/react";

function fmtDateTime(iso) {
  if (!iso) return "—";
  try {
    return new Date(iso).toLocaleString("en-IN", {
      year: "numeric", month: "short", day: "numeric",
      hour: "2-digit", minute: "2-digit",
    });
  } catch { return iso; }
}

export default function AdminAstrologersPage() {
  const { user, loading: authLoading } = useAuth();
  const navigate = useNavigate();
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [query, setQuery] = useState("");
  const [deleting, setDeleting] = useState(null);
  const [toast, setToast] = useState(null);

  useEffect(() => {
    if (authLoading) return;
    if (!user) { navigate("/"); return; }
    if (!user.is_super_admin) { navigate("/"); }
  }, [user, authLoading, navigate]);

  const load = async () => {
    setLoading(true);
    setError(null);
    try {
      const { data } = await api.get("/admin/astrologers");
      setItems(data.astrologers || []);
    } catch (e) {
      setError(e?.response?.data?.detail || "Could not load astrologers.");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { if (user?.is_super_admin) load(); }, [user]);

  const handleDelete = async (row) => {
    const isSeed = row.source === "ai_seed";
    const warn = isSeed
      ? `DELETE the AI SEED astrologer "${row.name}" (${row.email})?\n\nThis is one of the built-in AstroKuber AI guides and will be force-deleted along with ALL their chats, messages and reviews.\n\nThis cannot be undone.`
      : `Delete the registered astrologer "${row.name}" (${row.email})?\n\nThis will also remove all of their chats, messages and reviews. This cannot be undone.`;
    if (!window.confirm(warn)) return;
    setDeleting(row.astrologer_id);
    try {
      const { data } = await api.delete(
        `/admin/astrologers/${row.astrologer_id}`,
        { params: isSeed ? { force: true } : {} },
      );
      const d = data?.deleted || {};
      setToast({
        kind: "ok",
        text: `Removed "${row.name}" — ${d.chats || 0} chats, ${d.messages || 0} messages, ${d.reviews || 0} reviews scrubbed.`,
      });
      setItems((prev) => prev.filter((x) => x.astrologer_id !== row.astrologer_id));
    } catch (e) {
      setToast({ kind: "err", text: e?.response?.data?.detail || "Delete failed." });
    } finally {
      setDeleting(null);
      setTimeout(() => setToast(null), 6000);
    }
  };

  if (authLoading) {
    return (
      <PageShell><Header />
        <div className="max-w-5xl mx-auto px-6 pt-20 pb-24 text-center text-muted">Loading…</div>
        <Footer />
      </PageShell>
    );
  }

  if (!user || !user.is_super_admin) {
    return (
      <PageShell><Header />
        <div className="max-w-3xl mx-auto px-6 pt-20 pb-24 text-center" data-testid="admin-astrologers-denied">
          <ShieldWarning size={36} weight="duotone" className="mx-auto text-rose-600" />
          <h1 className="font-serif-display text-3xl mt-4 font-light text-[#3F2D1B]">Super-admin only</h1>
          <p className="text-muted mt-3 font-light max-w-md mx-auto leading-relaxed">
            This page lets you delete astrologer accounts and is restricted to AstroKuber super-admins.
          </p>
          <Link to="/" className="inline-block mt-7 px-6 py-2.5 rounded-full bg-[#3F2D1B] text-amber-50 text-sm font-bold tracking-wider">
            Back to home
          </Link>
        </div>
        <Footer />
      </PageShell>
    );
  }

  const q = query.trim().toLowerCase();
  const filtered = q
    ? items.filter((r) =>
        (r.name || "").toLowerCase().includes(q) ||
        (r.email || "").toLowerCase().includes(q) ||
        (r.astrologer_id || "").toLowerCase().includes(q),
      )
    : items;

  const registeredCount = items.filter((r) => r.source === "registered").length;
  const seedCount = items.filter((r) => r.source === "ai_seed").length;

  return (
    <PageShell><Header />
      <main className="max-w-6xl mx-auto px-5 sm:px-6 lg:px-10 pt-10 pb-20" data-testid="admin-astrologers-page">
        <header className="flex items-baseline justify-between gap-4 flex-wrap">
          <div>
            <div className="label-eyebrow text-[#7C2D12]">Super-admin · Danger zone</div>
            <h1 className="font-serif-display text-4xl sm:text-5xl mt-2 font-light leading-tight text-[#3F2D1B]">
              Delete astrologers <span className="text-muted text-base font-light">· {items.length}</span>
            </h1>
            <p className="text-muted mt-3 font-light text-sm max-w-2xl">
              Hard-delete an astrologer account along with every chat, message and review attached to it.
              Registered accounts (anyone who signed up via the public form) are shown first.
              {seedCount > 0 && registeredCount > 0 && (
                <> &nbsp;<b className="font-medium text-[#3F2D1B]">{registeredCount}</b> registered · <b className="font-medium text-[#3F2D1B]">{seedCount}</b> AI seed.</>
              )}
            </p>
          </div>
          <button
            type="button"
            onClick={load}
            data-testid="admin-astrologers-refresh"
            className="inline-flex items-center gap-2 px-4 py-2 rounded-full border border-amber-300 text-[#3F2D1B] text-xs font-bold tracking-wider hover:bg-amber-50"
          >
            <ArrowsClockwise size={14} weight="bold" /> REFRESH
          </button>
        </header>

        <div className="mt-7 relative max-w-md">
          <MagnifyingGlass size={16} weight="bold" className="absolute left-4 top-1/2 -translate-y-1/2 text-stone-400" />
          <input
            type="search"
            placeholder="Search name, email or ID…"
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            data-testid="admin-astrologers-search"
            className="w-full pl-10 pr-4 py-2.5 rounded-full border border-amber-200 bg-white text-sm focus:outline-none focus:ring-2 focus:ring-amber-300"
          />
        </div>

        {error && (
          <div className="mt-6 rounded-xl p-4 bg-rose-50 border border-rose-200 text-rose-900 text-sm" data-testid="admin-astrologers-error">
            {error}
          </div>
        )}

        {toast && (
          <div
            className={`mt-6 rounded-xl p-4 border text-sm ${toast.kind === "ok" ? "bg-emerald-50 border-emerald-200 text-emerald-900" : "bg-rose-50 border-rose-200 text-rose-900"}`}
            data-testid="admin-astrologers-toast"
          >
            {toast.text}
          </div>
        )}

        {loading ? (
          <div className="mt-12 text-center text-muted">Loading astrologers…</div>
        ) : filtered.length === 0 ? (
          <div className="mt-12 rounded-2xl p-10 bg-amber-50 border border-amber-200 text-center" data-testid="admin-astrologers-empty">
            <p className="font-serif-display text-2xl font-light text-[#3F2D1B]">No matches.</p>
            <p className="text-muted mt-2 text-sm font-light">Try a different search term, or refresh.</p>
          </div>
        ) : (
          <div className="mt-8 space-y-3">
            {filtered.map((row) => {
              const isSeed = row.source === "ai_seed";
              return (
                <article
                  key={row.astrologer_id}
                  data-testid={`admin-astro-row-${row.astrologer_id}`}
                  className={`rounded-2xl border overflow-hidden ${isSeed ? "border-amber-200 bg-white" : "border-rose-300 bg-rose-50/40"}`}
                >
                  <div className="p-4 sm:p-5 flex flex-wrap items-center gap-4">
                    <div className="flex items-center gap-3 min-w-0 flex-1">
                      {row.avatar ? (
                        <img
                          src={row.avatar}
                          alt={row.name}
                          className="w-12 h-12 rounded-full object-cover border border-amber-200"
                          onError={(e) => { e.currentTarget.style.display = "none"; }}
                        />
                      ) : (
                        <UserCircle size={48} weight="duotone" className="text-stone-400 shrink-0" />
                      )}
                      <div className="min-w-0">
                        <div className="flex items-baseline gap-2 flex-wrap">
                          <h2 className="font-serif-display text-xl font-light text-[#3F2D1B] truncate">{row.name || "Unnamed"}</h2>
                          {isSeed ? (
                            <span className="text-[10px] font-bold tracking-wider px-2 py-0.5 rounded-full bg-amber-200 text-amber-950 inline-flex items-center gap-1">
                              <Robot size={11} weight="bold" /> AI SEED
                            </span>
                          ) : (
                            <span className="text-[10px] font-bold tracking-wider px-2 py-0.5 rounded-full bg-rose-200 text-rose-950">
                              REGISTERED
                            </span>
                          )}
                        </div>
                        <div className="text-xs text-muted mt-0.5 truncate">
                          <a href={`mailto:${row.email}`} className="underline">{row.email}</a>
                          &nbsp;·&nbsp;<code className="font-mono">{row.astrologer_id}</code>
                        </div>
                        <div className="text-[11px] text-muted mt-0.5">
                          {row.specialization || "—"} · {row.experience_years || 0} yrs · ₹{row.rate_per_min || 0}/min · joined {fmtDateTime(row.created_at)}
                        </div>
                      </div>
                    </div>
                    <button
                      type="button"
                      onClick={() => handleDelete(row)}
                      disabled={deleting === row.astrologer_id}
                      data-testid={`admin-astrologers-delete-${row.astrologer_id}`}
                      className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-rose-600 text-white text-xs font-bold tracking-wider hover:bg-rose-700 disabled:opacity-60 disabled:cursor-wait"
                    >
                      <Trash size={14} weight="bold" /> {deleting === row.astrologer_id ? "DELETING…" : "DELETE"}
                    </button>
                  </div>
                </article>
              );
            })}
          </div>
        )}
      </main>
      <Footer />
    </PageShell>
  );
}
